import { Severity, SEVERITY_TO_LABEL } from '@/utils/constants'
import {
  EnforcementRequestStatus,
  EnforcementRequestStatusLabels,
  OnboardingStatus,
  OnboardingStatusLabels,
  ProductType,
  ReportClassification,
  Role,
  RoleLabels,
  JiraIssueStatus,
} from '@/generated/enums'
import {
  DOPPEL_BREACH_RED,
  DOPPEL_FIREWALL_ORANGE,
  DOPPEL_INACTIVE_GREY,
  DOPPEL_TEXT_WHITE,
  DOPPEL_TEAL_BLUE,
  FONT_SIZE_SMALL,
  DOPPEL_SECONDARY_GREEN,
  DOPPEL_BLANK_SHADE,
  DOPPEL_TAG_BACKGROUND_BLUE,
  DOPPEL_TAG_OUTLINE,
  BUTTON_COLOR_PRIMARY,
  DOPPEL_SECONDARY_GREEN_SHADE,
  DOPPEL_TAG_YELLOW,
  DOPPEL_TAG_YELLOW_SHADE,
  DOPPEL_TEXT_BLACK,
  DOPPEL_OFFICIAL_BLUE,
  DOPPEL_TAG_RED,
  DOPPEL_TAG_RED_SHADE,
  DOPPEL_TAG_PURPLE,
  DOPPEL_TAG_PURPLE_SHADE,
  DOPPEL_INTERNAL_PURPLE,
} from '../../utils/style'
import {
  Spinner,
  Tag,
  Box,
  MenuButton,
  Icon,
  Tooltip,
  TagProps,
} from '@chakra-ui/react'
import { unSnakeCase } from '@/utils/string_utils'
import { IoIosClose } from 'react-icons/io'
import { Dropdown, DropdownItem } from '@/components/doppel_design/dropdown'
import { ChevronDownIcon } from '@chakra-ui/icons'
import { useGetUsersQuery } from '@/generated/graphql'
import { getSortedAlertAssigneesWithUnassigned } from '@/utils/enforcement_utils'
import { useUserID } from '@/hooks/id_token_claims'
import { PRODUCT_TYPE_TO_REPORT_TYPE_MAP } from '@/utils/reports/report_utils'

const TAG_HEIGHT = '27px'
const TAG_FONT_SIZE = FONT_SIZE_SMALL
const TAG_BORDER_WIDTH = '0.5px'

// tag colours
const BLUE_CONFIG = {
  color: DOPPEL_OFFICIAL_BLUE,
  bgColor: DOPPEL_TAG_BACKGROUND_BLUE,
}

const YELLOW_CONFIG = {
  color: DOPPEL_TAG_YELLOW,
  bgColor: DOPPEL_TAG_YELLOW_SHADE,
}

const GREEN_CONFIG = {
  color: DOPPEL_SECONDARY_GREEN,
  bgColor: DOPPEL_SECONDARY_GREEN_SHADE,
}

const RED_CONFIG = {
  color: DOPPEL_TAG_RED,
  bgColor: DOPPEL_TAG_RED_SHADE,
}

const GREY_CONFIG = {
  color: DOPPEL_TAG_OUTLINE,
  bgColor: DOPPEL_BLANK_SHADE,
}

const PURPRLE_CONFIG = {
  color: DOPPEL_TAG_PURPLE,
  bgColor: DOPPEL_TAG_PURPLE_SHADE,
}

const DEFAULT_COLOR_CONFIG = GREY_CONFIG

const ONBOARDING_STATUS_TO_COLOR: Record<
  OnboardingStatus,
  { color: string; bgColor: string }
> = {
  [OnboardingStatus.PENDING]: YELLOW_CONFIG,
  [OnboardingStatus.ACTIVE]: GREEN_CONFIG,
  [OnboardingStatus.DEACTIVATED]: RED_CONFIG,
}

const REPORT_CLASSIFICATION_TO_COLOR: Record<
  ReportClassification,
  { color: string; bgColor: string }
> = {
  [ReportClassification.ACTIVE]: GREY_CONFIG,
  [ReportClassification.PARKED]: GREY_CONFIG,
  [ReportClassification.DOWN]: GREY_CONFIG,
  // Below classifications are deprecated
  [ReportClassification.UNCLASSIFIED]: GREY_CONFIG,
  [ReportClassification.UNRELATED]: GREY_CONFIG,
  [ReportClassification.UNKNOWN]: BLUE_CONFIG,
  [ReportClassification.RELATED]: YELLOW_CONFIG,
  [ReportClassification.SUSPICIOUS]: YELLOW_CONFIG,
}

const SEVERITY_TO_COLOR: Record<Severity, { color: string; bgColor: string }> = {
  [Severity.LOW]: BLUE_CONFIG,
  [Severity.MEDIUM]: YELLOW_CONFIG,
  [Severity.HIGH]: RED_CONFIG,
}

type DoppelTagProps = {
  tag: React.ReactNode
  loading?: boolean
  bgColor?: string
  color?: string
  width?: string
  height?: string
  deleteWidth?: string
  deleteHeight?: string
  onDelete?: () => Promise<void> | void
} & TagProps

export function DoppelTag({
  tag,
  loading,
  bgColor,
  color,
  width,
  height,
  deleteWidth,
  deleteHeight,
  onDelete,
  ...tagProps
}: DoppelTagProps) {
  const { color: defaultColor, bgColor: defaultBgColor } = DEFAULT_COLOR_CONFIG

  // if email, don't apply title case
  const isEmail = typeof tag === 'string' && tag.includes('@')
  const textTransformValue = isEmail ? 'none' : 'capitalize'

  return (
    <Tag
      borderRadius="4px"
      fontSize={FONT_SIZE_SMALL}
      fontWeight={600}
      paddingX={2}
      paddingY={1}
      style={{
        ...(width && { width: width }),
        height: height ?? TAG_HEIGHT,
        backgroundColor: bgColor ?? defaultBgColor,
        color: color ?? defaultColor,
        borderColor: color ?? defaultColor,
        borderWidth: TAG_BORDER_WIDTH,
      }}
      textTransform={textTransformValue}
      {...tagProps}
    >
      {loading ? (
        <Spinner size="xs" />
      ) : typeof tag === 'string' || typeof tag === 'number' ? (
        tag.toString()
      ) : (
        tag
      )}

      {onDelete && (
        <Box
          as={IoIosClose}
          borderRadius="4px"
          cursor="pointer"
          ml={1}
          mt={'0.5px'}
          onClick={onDelete}
          padding={0}
          style={{
            width: deleteWidth ?? 'inherit',
            height: deleteHeight ?? 'inherit',
          }}
        />
      )}
    </Tag>
  )
}

type DoppelTagWithDropdownProps = {
  dropdownItems: DropdownItem[]
  onDropdownItemSelect: (value: string) => void
  showDropdownChevron?: boolean
} & DoppelTagProps

export function DoppelTagWithDropdown({
  tag,
  dropdownItems,
  onDropdownItemSelect,
  showDropdownChevron,
  ...tagProps
}: DoppelTagWithDropdownProps) {
  const formattedTag = showDropdownChevron ? (
    <>
      {tag}{' '}
      <Icon
        as={ChevronDownIcon}
        bg={DOPPEL_INTERNAL_PURPLE}
        borderRadius={4}
        boxSize={4}
        ml={1}
      />
    </>
  ) : (
    tag
  )
  return (
    <Box>
      <Dropdown
        customMenuButton={
          <MenuButton>
            <DoppelTag tag={formattedTag} {...tagProps} />
          </MenuButton>
        }
        items={dropdownItems}
        onSelect={onDropdownItemSelect}
      />
    </Box>
  )
}

type DoppelTagWithTooltipProps = {
  tag: React.ReactNode
  tooltip?: string
  tooltipPlacement?: 'auto' | 'top' | 'right' | 'bottom' | 'left'
  tooltipDisabled?: boolean
} & DoppelTagProps
export function DoppelTagWithTooltip({
  tooltip,
  tooltipPlacement,
  tooltipDisabled,
  ...rest
}: DoppelTagWithTooltipProps) {
  if (!tooltip) return <DoppelTag {...rest} />
  return (
    <Tooltip
      hasArrow
      isDisabled={tooltipDisabled ?? false}
      label={tooltip}
      placement={tooltipPlacement ?? 'bottom'}
    >
      <span>
        <DoppelTag {...rest} />
      </span>
    </Tooltip>
  )
}

type SeverityTagProps = {
  severity: Severity
  setSeverity?: (severity: Severity) => void // will render a dropdown
} & Partial<DoppelTagWithDropdownProps>
export function SeverityTag({ severity, setSeverity, ...rest }: SeverityTagProps) {
  if (!severity) return null
  const formatText = (severity) =>
    `${unSnakeCase(SEVERITY_TO_LABEL[severity])} severity`
  const { color, bgColor } = SEVERITY_TO_COLOR[severity]
  const tagProps: DoppelTagProps = {
    tag: formatText(severity),
    bgColor: bgColor,
    color: color,
  }
  if (!setSeverity) {
    return <DoppelTag {...tagProps} {...rest} />
  }

  const severityOptions = Object.keys(SEVERITY_TO_COLOR) as Severity[]
  return (
    <DoppelTagWithDropdown
      dropdownItems={severityOptions.map((severityValue) => ({
        label: formatText(severityValue),
        value: severityValue,
      }))}
      onDropdownItemSelect={(value) => {
        setSeverity(value as Severity)
      }}
      {...tagProps}
      {...rest}
    />
  )
}

type ScoreTagProps = {
  score: number
} & Partial<DoppelTagProps>
const SCORE_COLOR_THRESHOLDS: Array<[number, { color: string; bgColor: string }]> = [
  [80, RED_CONFIG],
  [50, YELLOW_CONFIG],
  [0, BLUE_CONFIG],
]
export function ScoreTag({ score, ...rest }: ScoreTagProps) {
  const computedScore = score ?? 0
  const text = computedScore.toFixed(1)
  const [, tagConfig] =
    SCORE_COLOR_THRESHOLDS.find(([threshold]) => computedScore >= threshold) ||
    SCORE_COLOR_THRESHOLDS[SCORE_COLOR_THRESHOLDS.length - 1]
  return (
    <DoppelTag
      bgColor={tagConfig.bgColor}
      color={tagConfig.color}
      tag={text}
      {...rest}
    />
  )
}

type ClassificationTagProps = {
  classification: ReportClassification
  setClassification?: (classification: ReportClassification) => void // will render a dropdown
} & Partial<DoppelTagWithDropdownProps>
export function ClassificationTag({
  classification,
  setClassification,
  ...rest
}: ClassificationTagProps) {
  if (!classification) return null

  const formatText = (classification: ReportClassification) => {
    // Show 'active' for 'unknown' classification since ESC never returns 'active'
    if (classification === ReportClassification.UNKNOWN)
      return unSnakeCase(ReportClassification.ACTIVE)
    return unSnakeCase(classification) // will need external label mapping if exists
  }
  const { color, bgColor } = REPORT_CLASSIFICATION_TO_COLOR[classification]
  const tagProps: DoppelTagProps = {
    tag: formatText(classification),
    bgColor: bgColor,
    color: color,
  }
  if (!setClassification) {
    return <DoppelTag {...tagProps} {...rest} />
  }
  return (
    <DoppelTagWithDropdown
      dropdownItems={[
        ReportClassification.ACTIVE,
        ReportClassification.DOWN,
        ReportClassification.PARKED,
      ].map((key: ReportClassification) => ({
        label: formatText(key),
        value: key,
      }))}
      onDropdownItemSelect={(value) => {
        setClassification(value as ReportClassification)
      }}
      {...tagProps}
      {...rest}
    />
  )
}

type JiraTicketStatusTagProps = {
  status: string
} & Partial<DoppelTagProps>
const JIRA_TICKET_STATUS_COLOR_MAP = {
  [JiraIssueStatus.TO_DO]: DOPPEL_BREACH_RED,
  [JiraIssueStatus.REJECTED]: DOPPEL_BREACH_RED,
  [JiraIssueStatus.IN_PROGRESS]: DOPPEL_FIREWALL_ORANGE,
  [JiraIssueStatus.IN_REVIEW]: DOPPEL_FIREWALL_ORANGE,
  [JiraIssueStatus.DONE]: DOPPEL_SECONDARY_GREEN,
  [JiraIssueStatus.ARCHIVED]: DOPPEL_SECONDARY_GREEN,
  [JiraIssueStatus.CANCELED]: DOPPEL_SECONDARY_GREEN,
  defaultColor: DOPPEL_INACTIVE_GREY,
}
export function JiraTicketStatusTag({ status, ...rest }: JiraTicketStatusTagProps) {
  if (!status) return null
  const textColor = DOPPEL_TEXT_WHITE
  const tagColor =
    JIRA_TICKET_STATUS_COLOR_MAP[status] || JIRA_TICKET_STATUS_COLOR_MAP.defaultColor
  return <DoppelTag bgColor={tagColor} color={textColor} tag={status} {...rest} />
}

type EnforcementStatusTagProps = {
  status: string
  isEmployeeView: boolean
} & Partial<DoppelTagProps>
const ENFORCEMENT_STATUS_COLOR_MAP = {
  [EnforcementRequestStatus.STAGED]: DOPPEL_TEAL_BLUE,
  [EnforcementRequestStatus.REPORTED]: DOPPEL_BLANK_SHADE,
  [EnforcementRequestStatus.BLOCKED]: DOPPEL_FIREWALL_ORANGE,
  [EnforcementRequestStatus.APPROVED]: DOPPEL_SECONDARY_GREEN,
  [EnforcementRequestStatus.CANCELED]: DOPPEL_BREACH_RED,
  [EnforcementRequestStatus.REJECTED]: DOPPEL_BREACH_RED,
  [EnforcementRequestStatus.RETRACTION_SENT]: DOPPEL_FIREWALL_ORANGE,
  [EnforcementRequestStatus.RETRACTED]: DOPPEL_BREACH_RED,
  [EnforcementRequestStatus.REROUTED]: DOPPEL_SECONDARY_GREEN,
  [EnforcementRequestStatus.FAILED]: DOPPEL_BREACH_RED,
  [EnforcementRequestStatus.NO_RESPONSE]: DOPPEL_FIREWALL_ORANGE,
  defaultColor: DOPPEL_INACTIVE_GREY,
}

export function EnforcementStatusTag({
  status,
  isEmployeeView,
  ...rest
}: EnforcementStatusTagProps) {
  if (!status) return null
  const textColor = DOPPEL_TEXT_WHITE
  if (!isEmployeeView) {
    status = EnforcementRequestStatusLabels[status]
  }
  const tagColor =
    ENFORCEMENT_STATUS_COLOR_MAP[status] || ENFORCEMENT_STATUS_COLOR_MAP.defaultColor
  const text = unSnakeCase(status)
  return <DoppelTag bgColor={tagColor} color={textColor} tag={text} {...rest} />
}

type InternalAssigneeTagProps = {
  internalAssignee: { id: string; email: string }
  setInternalAssignee?: (internalAssigneeId: string) => void
} & Partial<DoppelTagWithDropdownProps>
export function InternalAssigneeTag({
  internalAssignee,
  setInternalAssignee,
  ...rest
}: InternalAssigneeTagProps) {
  const [userId] = useUserID()
  const tagProps: DoppelTagProps = {
    tag: internalAssignee.email,
    bgColor: PURPRLE_CONFIG.bgColor,
    color: PURPRLE_CONFIG.color,
  }

  const { data: assigneeData } = useGetUsersQuery({
    variables: {
      usersWhere: { is_internal: { _eq: true } },
    },
  })

  const alertAssignees = assigneeData?.users || []

  const sortedAssignees = getSortedAlertAssigneesWithUnassigned(alertAssignees, userId)

  return (
    <DoppelTagWithDropdown
      dropdownItems={sortedAssignees.map((key) => ({
        label: key.email,
        value: key.id,
      }))}
      onDropdownItemSelect={(value) => {
        setInternalAssignee(value)
      }}
      {...tagProps}
      {...rest}
    />
  )
}

type ExternalAssigneeTagProps = {
  externalAssignee: { id: string; email: string }
  organizationId: string
  setExternalAssignee?: (externalAssigneeId: string) => void
} & Partial<DoppelTagWithDropdownProps>
export function ExternalAssigneeTag({
  externalAssignee,
  organizationId,
  setExternalAssignee,
  ...rest
}: ExternalAssigneeTagProps) {
  const [userId] = useUserID()
  const tagProps: DoppelTagProps = {
    tag: externalAssignee.email,
    bgColor: GREY_CONFIG.bgColor,
    color: GREY_CONFIG.color,
  }

  const { data: assigneeData } = useGetUsersQuery({
    variables: {
      usersWhere: {
        is_internal: { _eq: false },
        organization_id: { _eq: organizationId },
      },
    },
  })

  const alertAssignees = assigneeData?.users || []

  const sortedAssignees = getSortedAlertAssigneesWithUnassigned(alertAssignees, userId)

  // If not setExternalAssignee, just render the tag without the dropdown
  if (!setExternalAssignee) {
    return <DoppelTag {...tagProps} {...rest} />
  }

  return (
    <DoppelTagWithDropdown
      dropdownItems={sortedAssignees.map((key) => ({
        label: key.email,
        value: key.id,
      }))}
      onDropdownItemSelect={(value) => {
        setExternalAssignee(value)
      }}
      {...tagProps}
      {...rest}
    />
  )
}

type RoleTagProps = {
  role: Role
} & Partial<DoppelTagProps>
export const RoleTag = ({ role, ...props }: RoleTagProps) => {
  return (
    <DoppelTag
      bgColor={DOPPEL_TAG_BACKGROUND_BLUE}
      borderColor={DOPPEL_OFFICIAL_BLUE}
      borderWidth={TAG_BORDER_WIDTH}
      color={DOPPEL_OFFICIAL_BLUE}
      fontSize={TAG_FONT_SIZE}
      height={TAG_HEIGHT}
      tag={RoleLabels[role]}
      {...props}
    />
  )
}

type ReportClassificationTagProps = {
  classification: string
} & Partial<DoppelTagProps>
export const ReportClassificationTag = ({
  classification,
  ...props
}: ReportClassificationTagProps) => {
  if (!classification) return null // until statuses are always set, need null check
  const classificationKey = classification as ReportClassification
  const { color, bgColor } =
    REPORT_CLASSIFICATION_TO_COLOR[classificationKey] || DEFAULT_COLOR_CONFIG
  return (
    <DoppelTag
      bgColor={bgColor}
      borderColor={color}
      borderWidth={TAG_BORDER_WIDTH}
      color={color}
      fontSize={TAG_FONT_SIZE}
      height={TAG_HEIGHT}
      tag={classificationKey}
      {...props}
    />
  )
}

type OnboardingStatusTagProps = {
  status: string
} & Partial<DoppelTagProps>
export const OnboardingStatusTag = ({ status, ...props }: OnboardingStatusTagProps) => {
  if (!status) return null // until statuses are always set, need null check
  const { color, bgColor } = ONBOARDING_STATUS_TO_COLOR[status]
  return (
    <DoppelTag
      bgColor={bgColor}
      borderColor={color}
      borderWidth={TAG_BORDER_WIDTH}
      color={color}
      fontSize={TAG_FONT_SIZE}
      height={TAG_HEIGHT}
      tag={OnboardingStatusLabels[status]}
      {...props}
    />
  )
}

type ModuleTagProps = {
  product: ProductType | string
} & Partial<DoppelTagProps>
export const ModuleTag = ({ product, ...props }: ModuleTagProps) => {
  const reportTypeFromProductType = PRODUCT_TYPE_TO_REPORT_TYPE_MAP[product] || product
  return (
    <DoppelTag
      bgColor={DOPPEL_BLANK_SHADE}
      borderColor={DOPPEL_TAG_OUTLINE}
      borderWidth={TAG_BORDER_WIDTH}
      color={DOPPEL_TEXT_WHITE}
      fontSize={TAG_FONT_SIZE}
      height={TAG_HEIGHT}
      tag={reportTypeFromProductType}
      {...props}
    />
  )
}

type DeletableModuleTagProps = {
  onDelete: () => void
} & ModuleTagProps
export function DeletableModuleTag({
  onDelete,
  product,
  ...props
}: DeletableModuleTagProps) {
  const reportTypeFromProductType = PRODUCT_TYPE_TO_REPORT_TYPE_MAP[product] || product
  return (
    <DoppelTag
      bgColor={BUTTON_COLOR_PRIMARY}
      borderColor={DOPPEL_BLANK_SHADE}
      borderRadius={'8px'}
      borderWidth={'1px'}
      color={DOPPEL_TEXT_BLACK}
      deleteHeight={'30px'}
      deleteWidth={'30px'}
      fontSize={'16px'}
      fontWeight={500}
      height={'40px'}
      lineHeight={'20px'}
      onDelete={onDelete}
      paddingLeft={'12px'}
      tag={reportTypeFromProductType}
      {...props}
    />
  )
}
